/**
 * Admin step-up MFA — the pure half.
 *
 * Two things live here, and neither touches the network:
 *
 *  1. The handoff between request services (plain modules) and the dialog (a
 *     mounted React component). Both admin bundles mount <AdminMfaDialog/>,
 *     which registers a handler; an axios/fetch/XHR caller sees a 403 carrying
 *     an admin-MFA code, asks for an elevation, and replays the failed request
 *     when the handler resolves true.
 *  2. The copy for a verification failure, since each server code implies a
 *     different next action.
 *
 * Elevation is single-flight on purpose: a tab that fires five admin requests
 * at once must raise ONE code prompt, not five. Same idiom as the 401-refresh
 * queue in the app client.
 */

// The two codes the server puts on a 403 from an admin route.
// See requireAdmin() in api/src/modules/admin/controllers/coachControls.js.
export const ADMIN_MFA_CODES = ['ADMIN_MFA_REQUIRED', 'ADMIN_MFA_ENROLLMENT_REQUIRED'];

export const MFA_CODE_LENGTH = 6;

let handler = null;
let pending = null;
let opener = null;

/**
 * Register the dialog's elevation handler.
 *
 * @param {(code: string) => Promise<boolean>} fn resolves true once the session
 *   is elevated, false when the admin backs out.
 * @returns {() => void} unregister, for the effect cleanup.
 */
export const registerAdminMfaHandler = (fn) => {
  handler = fn;
  return () => {
    if (handler === fn) { handler = null; }
  };
};

/**
 * Register the dialog's proactive-open handler.
 *
 * The 403 handler above only fires when a request has already been refused,
 * which makes two ordinary things impossible: enrolling BEFORE the flag is
 * switched on (no refusal can happen yet), and replacing a working
 * authenticator (an elevated session is never refused). This is the entry
 * point the account menu uses for both.
 *
 * @param {() => void} fn
 * @returns {() => void} unregister, for the effect cleanup.
 */
export const registerAdminMfaOpener = (fn) => {
  opener = fn;
  return () => {
    if (opener === fn) { opener = null; }
  };
};

/**
 * Open the two-factor dialog on purpose, rather than in response to a refusal.
 *
 * @returns {boolean} false when the calling bundle forgot to mount the dialog.
 */
export const openAdminMfa = () => {
  if (!opener) { return false; }
  opener();
  return true;
};

/**
 * The step-up code carried by a failed request, or null when the failure is
 * something else.
 *
 * The MFA endpoints themselves are excluded: POST /admin/mfa/enroll answers
 * ADMIN_MFA_REQUIRED when an enrolled admin tries to replace their
 * authenticator, and re-entering the gate from inside the dialog would loop.
 *
 * @param {any} error axios error
 * @returns {string|null}
 */
export const adminMfaCodeFrom = (error) => {
  const response = error?.response;
  if (!response || response.status !== 403) { return null; }
  const code = response.data?.code;
  if (!ADMIN_MFA_CODES.includes(code)) { return null; }
  if (String(error?.config?.url || '').includes('/admin/mfa/')) { return null; }
  return code;
};

/**
 * Ask the mounted dialog for an elevation. Resolves false when nothing is
 * mounted (the request just fails as it did before) or the admin cancels.
 *
 * @param {string} code
 * @returns {Promise<boolean>}
 */
/**
 * Whether a bundle has an elevation dialog mounted at all.
 *
 * requestElevation() returns false for two very different reasons: no dialog
 * exists, or the admin cancelled the one that does. Only the first is worth
 * telling someone about, and only this distinguishes them.
 *
 * @returns {boolean}
 */
export const isAdminMfaDialogMounted = () => { return Boolean(handler); };

export const requestElevation = (code) => {
  if (!handler) { return Promise.resolve(false); }
  if (!pending) {
    pending = Promise.resolve()
      .then(() => { return handler(code); })
      .then((elevated) => { return Boolean(elevated); })
      .catch(() => { return false; })
      .finally(() => { pending = null; });
  }
  return pending;
};

/**
 * The server code on a verification failure, or null.
 *
 * @param {any} error axios error
 * @returns {string|null}
 */
export const adminMfaErrorCode = (error) => {
  return error?.response?.data?.code || null;
};

/**
 * Digits only, six max. Makes a pasted "123 456" land clean.
 *
 * @param {any} value
 * @returns {string}
 */
export const normalizeMfaCode = (value) => {
  return String(value ?? '').replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);
};

/**
 * Human copy for a verification failure. The server sends a message, but these
 * codes each need a specific next action, and two of them (lockout, expired
 * enrollment) change what the dialog offers.
 *
 * @param {any} error axios error
 * @returns {string}
 */
export const describeAdminMfaError = (error) => {
  const data = error?.response?.data || {};
  const remaining = Number.isInteger(data.remaining) ? data.remaining : null;

  switch (data.code) {
    case 'ADMIN_MFA_INVALID_CODE':
      return remaining && remaining > 0
        ? `Incorrect code. ${remaining} ${remaining === 1 ? 'try' : 'tries'} left.`
        : 'Incorrect code. Check your authenticator and try again.';
    case 'ADMIN_MFA_CODE_ALREADY_USED':
      return 'That code was already used. Wait for the next one.';
    case 'ADMIN_MFA_NO_PENDING_ENROLLMENT':
      return 'This setup expired. Start again for a new key.';
    case 'ADMIN_MFA_LOCKED_OUT':
      return 'Too many incorrect codes. Try again in 15 minutes.';
    case 'ADMIN_MFA_UNAVAILABLE':
      return 'We can\'t check your code right now. Try again in a moment.';
    // Step-down could not revoke. Says so rather than claiming the session is
    // locked, because it stays elevated until the TTL runs out.
    case 'ADMIN_MFA_STEP_DOWN_FAILED':
      return 'Could not end the elevated session. It will expire on its own.';
    case 'ADMIN_MFA_ENROLLMENT_REQUIRED':
      return 'No authenticator is set up on this account yet.';
    // Only reachable from the enroll call: replacing an authenticator is the
    // move a session thief would make, so it needs a live elevation first.
    case 'ADMIN_MFA_REQUIRED':
      return 'Verify your current authenticator before replacing it.';
    default:
      return 'Something went wrong. Try again.';
  }
};
